import React, { useEffect, useState } from "react";
import { Loader2, Save, Trash2, ShieldCheck, Clock, BarChart3 } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { useToast } from "../../hooks/use-toast";

// Audit G02: CASS confirmed 90 days. Raw searches and transcripts are deleted
// on a schedule; anonymous monthly counts survive so the insight isn't lost.
export default function RetentionManager() {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [busy, setBusy] = useState("");

  const load = () =>
    adminApi.retention.get().then((d) => {
      setData(d);
      setDays(d.settings.days);
    }).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy("save");
    try {
      const d = await adminApi.retention.save(patch);
      setData(d);
      setDays(d.settings.days);
      toast({ title: "Retention settings saved" });
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Couldn't save", variant: "destructive" });
    } finally { setBusy(""); }
  };

  const purge = async () => {
    if (!window.confirm(`Delete all searches and chat transcripts older than ${data.settings.days} days now? This cannot be undone.`)) return;
    setBusy("purge");
    try {
      const r = await adminApi.retention.purge(false);
      await load();
      toast({ title: "Records deleted", description: `${r.chat_messages} transcripts, ${r.unmatched_terms} search terms, ${r.link_clicks} link clicks.` });
    } catch {
      toast({ title: "Couldn't run the purge", variant: "destructive" });
    } finally { setBusy(""); }
  };

  if (!data) return <p className="text-sm text-slate-400">Loading…</p>;

  const { settings, due, totals, insights } = data;

  return (
    <div data-testid="retention-manager">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-bold text-navy flex items-center gap-2">
              <ShieldCheck size={16} className="text-orange" /> Search &amp; chat retention
            </h3>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-3xl">
              Unmatched search wording and Cassie transcripts are deleted once they pass the retention period. The
              clean-up runs automatically at 3am each day, and anonymous monthly counts are kept so you don't lose the
              insight. Enquiries and client correspondence are not touched — they're kept under their own retention
              arrangements.
            </p>
          </div>
          <Button onClick={purge} disabled={!!busy} variant="outline" className="rounded-full" data-testid="retention-purge">
            {busy === "purge" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Trash2 size={14} className="mr-1.5" />} Delete eligible records now
          </Button>
        </div>

        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: "Retention period", value: `${settings.days} days` },
            { label: "Transcripts held", value: totals.chat_messages },
            { label: "Search terms held", value: totals.unmatched_terms },
            { label: "Due for deletion", value: due.chat_messages + due.unmatched_terms + due.link_clicks },
          ].map((s) => (
            <div key={s.label} className="rounded-xl bg-slate-50 p-4" data-testid={`retention-stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>
              <p className="text-xl font-display font-bold text-navy">{s.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-5 grid md:grid-cols-3 gap-4">
          <label className="block">
            <span className="text-xs font-semibold text-navy">Delete after (days)</span>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="number"
                min={7}
                max={730}
                value={days}
                onChange={(e) => setDays(e.target.value)}
                className="w-28 border border-slate-200 rounded-xl px-3 py-2 text-sm"
                data-testid="retention-days"
              />
              <Button onClick={() => save({ days: Number(days) })} disabled={!!busy} size="sm" className="bg-orange hover:bg-orange-dark text-white rounded-full" data-testid="retention-save">
                {busy === "save" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Save size={13} className="mr-1" />} Save
              </Button>
            </div>
          </label>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-navy">Automatic deletion</p>
              <p className="text-xs text-slate-400">Runs daily at 3am.</p>
            </div>
            <Switch checked={!!settings.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="retention-enabled" />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-navy">Keep anonymous counts</p>
              <p className="text-xs text-slate-400">Monthly totals only — no wording.</p>
            </div>
            <Switch checked={settings.keep_insights !== false} onCheckedChange={(v) => save({ keep_insights: v })} data-testid="retention-insights" />
          </div>
        </div>

        {settings.last_run && (
          <p className="mt-4 text-xs text-slate-400 flex items-center gap-1.5" data-testid="retention-last-run">
            <Clock size={13} /> Last clean-up {new Date(settings.last_run).toLocaleString("en-GB")} — removed{" "}
            {(settings.last_result?.chat_messages || 0)} transcripts and {(settings.last_result?.unmatched_terms || 0)} search terms.
          </p>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <h3 className="font-display font-bold text-navy flex items-center gap-2">
          <BarChart3 size={16} className="text-orange" /> What survives deletion
        </h3>
        <p className="text-xs text-slate-500 mt-1">Anonymous monthly counts — no message text, no search wording, no identifiers.</p>
        {insights.length === 0 ? (
          <p className="text-sm text-slate-400 mt-4">Nothing rolled up yet. Counts appear here after the first clean-up.</p>
        ) : (
          <div className="mt-4 space-y-2" data-testid="retention-insights">
            {insights.map((i) => (
              <div key={i.period} className="flex flex-wrap items-center gap-4 rounded-xl bg-slate-50 px-4 py-3 text-xs">
                <span className="font-mono font-bold text-navy">{i.period}</span>
                <span className="text-slate-600">{i.chat_messages || 0} chat messages</span>
                <span className="text-slate-600">{i.unmatched_terms || 0} unmatched terms</span>
                <span className="text-slate-600">{i.unmatched_searches || 0} unmatched searches</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
